import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import type { ContentDetail, ContentTypeDefinition } from '@cms/contracts'
import { useI18n } from '../i18n/context.js'
import { ApiError, newIdempotencyKey } from '../api/client.js'
import {
  changeContentStatus,
  generateContentField,
  getContent,
  listAiAccounts,
  listContentTypes,
  restoreContent,
  setContentField,
  trashContent,
  type AiAccountOption,
} from '../api/endpoints.js'
import { buildFormModel, isEditable } from '../domain/field-model.js'
import { parseVersionConflict, retryWithServerVersion } from '../domain/conflict.js'
import type { PendingEdit, VersionConflictInfo } from '../domain/conflict.js'
import { labelForPath } from '../domain/field-labels.js'
import { collectTechnical, editableImagePath, getAtPath, isBag, isChanged, isTechnicalKey, labelForKey, payloadFor, setAtPath, type Bag } from '../domain/humane.js'
import { plainTextOf } from '../domain/html.js'
import { absoluteDateTime } from '../domain/format.js'
import { contentPath, displayDomain, liveUrl } from '../domain/live-url.js'
import { EditOnSiteButton } from '../components/EditOnSite.js'
import { fieldChangeReason, resolveReason } from '../domain/reason.js'
import { showPrimarySavebar } from '../domain/repository-mapping.js'
import { isContentTab, paths, type ContentTab } from '../routes.js'
import { FieldControl, fieldLabel } from '../components/FieldControl.js'
import { OtherFieldsSection } from '../components/OtherFieldsSection.js'
import { ContentTranslationsCard } from '../components/ContentTranslationsCard.js'
import { MediaFieldControl } from '../components/MediaFieldControl.js'
import { GroupBox, ObjectFields, TechnicalDetails } from '../components/ValueEditor.js'
import { ConflictDialog } from '../components/ConflictDialog.js'
import { ConfirmDialog } from '../components/ConfirmDialog.js'
import { DirtyNavigationGuard } from '../components/DirtyNavigationGuard.js'
import { RepositoryMappingPanel } from '../components/RepositoryMappingPanel.js'
import { ScheduleCard } from '../components/ScheduleCard.js'
import { useCan } from '../auth/session.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { PublishDialog } from './PublishDialog.js'
import { ReasonNote, SelectField, TextArea, TextField } from '../ui/fields.js'
import { MenuButton, useSnackbar, type MenuEntry } from '../ui/overlays.js'
import { Thumb } from '../components/Thumb.js'
import { mainImageOfContent } from '../domain/main-image.js'
import { Banner, Button, Card, ExternalButton, Facts, FilterChip, LinkButton, PageHeader, PageSkeleton, StatusChip, cx } from '../ui/primitives.js'
import { contentStatusTone, contentTypeIcon } from '../ui/status.js'

type Status = ContentDetail['status']
const WORKFLOW: Exclude<Status, 'trashed'>[] = ['draft', 'review', 'published']

/** `/s/:siteId/contenuti/:id` — the form, generated from the content type. */
export function ContentEditScreen() {
  const { id = '' } = useParams<{ id: string }>()
  return <ExistingContentEditor key={id} id={id}/>
}

function ExistingContentEditor({ id }: { id: string }) {
  const { t, locale } = useI18n()
  const snackbar = useSnackbar()
  const [searchParams] = useSearchParams()
  const upToParam = searchParams.get('upTo')
  const upTo = upToParam && /^\d+$/.test(upToParam) ? Number(upToParam) : undefined
  const tabParam = searchParams.get('tab')
  const canWrite = useCan('cms:content:write')
  const canPublish = useCan('cms:publish')
  const { siteId, site, reload: reloadWorkspace } = useSiteWorkspace()
  const viewingPast = upTo !== undefined
  // Two reasons to look without touching — an old version, or a role that
  // only reads — and one behaviour: fields disabled, no actions.
  const readOnly = viewingPast || !canWrite
  // Old links carry ?tab=seo / ?tab=strumenti: the page is one scroll now, so they jump to the section.
  const jumpTo = isContentTab(tabParam) && tabParam !== 'contenuto' && !(tabParam === 'strumenti' && readOnly) ? tabParam : null
  const [toolsOpen, setToolsOpen] = useState(jumpTo === 'strumenti')

  const [content, setContent] = useState<ContentDetail | null>(null)
  const [contentType, setContentType] = useState<ContentTypeDefinition | null>(null)
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [pendingStatus, setPendingStatus] = useState<Status>('draft')
  const [note, setNote] = useState('')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [actionBusy, setActionBusy] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)
  const [conflict, setConflict] = useState<{ info: VersionConflictInfo; pending: PendingEdit; remaining: PendingEdit[] } | null>(null)
  const [confirmingTrash, setConfirmingTrash] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [aiPurpose, setAiPurpose] = useState<'write' | 'review'>('write')
  const [aiAccounts, setAiAccounts] = useState<AiAccountOption[]>([])
  const [aiAccountId, setAiAccountId] = useState('')
  const [aiPath, setAiPath] = useState('')
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  const [repositoryDirty, setRepositoryDirty] = useState(false)
  const [repositoryExpanded, setRepositoryExpanded] = useState(false)
  /** The status to apply once the field writes are through, and the reasons chosen when Save was pressed. */
  const afterFields = useRef<{ status: Status | null; statusReason: string } | null>(null)

  const load = useCallback(async (): Promise<ContentDetail | null> => {
    setLoadError(null)
    try {
      const [detail, types] = await Promise.all([getContent(id, upTo), listContentTypes()])
      setContent(detail)
      setPendingStatus(detail.status)
      setValues({ ...detail.data })
      setContentType(types.find((ct) => ct.key === detail.contentTypeKey) ?? null)
      return detail
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : t('edit.loadError'))
      return null
    }
  }, [id, upTo, t])

  useEffect(() => {
    setContent(null)
    setContentType(null)
    void load()
  }, [load])

  useEffect(() => {
    if (readOnly || !toolsOpen) return
    listAiAccounts(aiPurpose)
      .then((result) => {
        setAiAccounts(result.accounts)
        const first = result.accounts.find((account) => account.status === 'ready' || account.status === 'available')
        setAiAccountId(first?.id ?? '')
      })
      .catch(() => { setAiAccounts([]); setAiAccountId('') })
  }, [aiPurpose, readOnly, toolsOpen])

  const fields = contentType?.fields ?? []
  // A value that was `null` and is still empty is not a change, and what the person never wrote is never sent.
  const changedPaths = useMemo(() => content
    ? Object.keys(values).filter((path) => isChanged(values[path], content.data[path]))
    : [], [content, values])
  const fieldsDirty = changedPaths.length > 0
  const statusDirty = !!content && pendingStatus !== content.status && content.status !== 'trashed'
  const primaryDirty = fieldsDirty || statusDirty
  const dirty = primaryDirty || repositoryDirty

  const fieldName = (path: string) => {
    const declared = fields.find((field) => field.key === path)
    return declared ? fieldLabel(declared, locale) : labelForKey(t, path)
  }
  const changedLabels = changedPaths.map(fieldName)
  const fieldReason = fieldChangeReason(t, changedLabels)
  const statusReason = t('reason.status', { status: t(`status.${pendingStatus}`) })
  const previewReason = fieldsDirty ? fieldReason : statusDirty ? statusReason : fieldReason

  const formModel = useMemo(() => content ? buildFormModel(fields, content.data) : null, [content, fields])

  const finishSave = async (version: number) => {
    const next = afterFields.current
    afterFields.current = null
    if (content && next?.status && next.status !== content.status) {
      try {
        await changeContentStatus(id, { status: next.status, reason: next.statusReason, expectedVersion: version }, newIdempotencyKey())
      } catch (err) {
        setSaveError(err instanceof ApiError ? t('editor.saveFailed', { reason: err.message }) : t('editor.saveFailedGeneric'))
        setSaving(false)
        await load()
        return
      }
    }
    setSaving(false)
    setNote('')
    const fresh = await load()
    setJustSaved(true)
    snackbar(fresh?.status === 'published' ? t('edit.savedPublishedHint') : t('edit.saved'))
  }

  const runQueue = async (queue: PendingEdit[], startVersion: number) => {
    let version = startVersion
    for (let i = 0; i < queue.length; i++) {
      const edit = queue[i]
      if (!edit) continue
      try {
        const result = await setContentField(id, { path: edit.path, value: edit.value, reason: edit.reason, expectedVersion: version }, newIdempotencyKey())
        version = result.version
      } catch (err) {
        const info = parseVersionConflict(err)
        if (info) {
          setConflict({ info, pending: { ...edit, expectedVersion: version }, remaining: queue.slice(i + 1) })
          setSaving(false)
          return
        }
        setSaveError(err instanceof ApiError ? t('editor.saveFailed', { reason: err.message }) : t('editor.saveFailedGeneric'))
        setSaving(false)
        return
      }
    }
    await finishSave(version)
  }

  const handleSave = async (targetStatus: Status = pendingStatus) => {
    if (!content || readOnly) return
    if (repositoryDirty) { setSaveError(t('repositoryMapping.saveMappingFirst')); return }
    const statusChange = targetStatus !== content.status && content.status !== 'trashed'
    if (!fieldsDirty && !statusChange) return
    setSaveError(null)
    setJustSaved(false)
    setSaving(true)
    const reason = resolveReason(note, fieldReason)
    afterFields.current = {
      status: statusChange ? targetStatus : null,
      statusReason: resolveReason(note, t('reason.status', { status: t(`status.${targetStatus}`) })),
    }
    const queue = changedPaths.flatMap((path) => {
      const value = payloadFor(values[path], content.data[path])
      return value === undefined ? [] : [{ path, value, reason, expectedVersion: content.version }]
    })
    await runQueue(queue, content.version)
  }

  // Ctrl/Cmd+S saves, as in every editor people already know.
  const saveRef = useRef(handleSave)
  saveRef.current = handleSave
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        void saveRef.current()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const handleKeepMine = async () => {
    if (!conflict) return
    setSaving(true)
    const retried = retryWithServerVersion(conflict.pending, conflict.info)
    setConflict(null)
    await runQueue([retried, ...conflict.remaining], conflict.info.actualVersion)
  }

  const handleKeepTheirs = () => {
    setConflict(null)
    setSaving(false)
    afterFields.current = null
    void load()
  }

  const runAction = async (action: () => Promise<unknown>, done: string) => {
    if (dirty) { setSaveError(t('edit.unsaved')); return }
    setActionBusy(true)
    setSaveError(null)
    try {
      await action()
      await load()
      setNote('')
      snackbar(done)
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : t('errors.generic'))
    } finally {
      setActionBusy(false)
    }
  }

  const handleAi = async () => {
    if (!content || !aiAccountId || !aiPath || !aiPrompt.trim()) return
    if (dirty) { setSaveError(t('edit.unsaved')); return }
    setAiBusy(true)
    setSaveError(null)
    try {
      await generateContentField(id, {
        accountId: aiAccountId,
        purpose: aiPurpose,
        path: aiPath,
        prompt: aiPrompt,
        reason: resolveReason(note, t(aiPurpose === 'review' ? 'reason.aiReview' : 'reason.aiWrite', { field: labelForPath(fields, aiPath, locale) })),
        expectedVersion: content.version,
      }, newIdempotencyKey())
      setAiPrompt('')
      setNote('')
      await load()
      snackbar(t('ai.done'))
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : t('errors.generic'))
    } finally {
      setAiBusy(false)
    }
  }

  useEffect(() => {
    if (!content || !jumpTo) return
    document.getElementById(jumpTo === 'seo' ? 'sezione-seo' : 'sezione-strumenti')?.scrollIntoView({ block: 'start' })
  }, [content?.id, jumpTo])

  if (loadError) {
    return <Banner tone="error" title={t('edit.loadError')} action={<LinkButton variant="text" to={paths.contents(siteId)}>{t('edit.backToList')}</LinkButton>}>{loadError}</Banner>
  }
  if (!content || !formModel) return <PageSkeleton label={t('list.loading')}/>
  if (content.siteId !== siteId) return <Navigate to={paths.content(content.siteId, content.id)} replace/>

  const title = contentTitle(content)
  const typeName = contentType?.label[locale] ?? contentType?.label.it ?? content.contentTypeKey
  const path = contentPath(contentType?.urlPattern, values, content.slug)
  const url = liveUrl(site?.primaryDomain, path)
  const busy = saving || actionBusy || aiBusy
  const declaredKeys = new Set(fields.map((field) => field.key))
  const bodyFormat = bodyFormatOf(values)

  // The main picture and the title lead the page; whatever field holds them is not repeated further down.
  const mainImage = mainImageOfContent(values, contentType?.seo?.image)
  const declaredMedia = fields.find((field) => field.type.toLowerCase() === 'media' && !isSeoPath(field.key))?.key
  const imageKey = editableImagePath(values, mainImage?.source) ?? (contentType?.seo?.image ?? declaredMedia ?? null)
  // A picture found by scanning an imported structure is shown but not editable here: nobody knows which field to write.
  const showImage = !!imageKey || !!mainImage
  const imageRoot = imageKey ? imageKey.split('.')[0] ?? null : null
  const imageTopLevel = imageKey !== null && !imageKey.includes('.')
  const titleEntry = formModel.known.find((entry) => entry.path === 'title')
  const hasTitle = !!titleEntry || typeof values.title === 'string'
  const imageEditable = !readOnly && !!imageKey && !(imageRoot && formModel.known.some((entry) => entry.path === imageRoot && !isEditable(entry.field)))

  const seoEntries = formModel.known.filter((entry) => isSeoPath(entry.path))
  const contentEntries = formModel.known.filter((entry) => !isSeoPath(entry.path) && entry.path !== 'title' && !(imageTopLevel && entry.path === imageKey))
  const seoExtras = formModel.extra.filter((entry) => isSeoPath(entry.path) && !isTechnicalKey(entry.path))
  const extraSeo = isBag(values.extra) && isBag(values.extra.seo) ? values.extra.seo : null
  const otherExtras = formModel.extra.filter((entry) => !isSeoPath(entry.path) && !(entry.path === 'title' && hasTitle) && !(imageTopLevel && entry.path === imageKey))
  const technical = collectTechnical(values, declaredKeys)
  const setValue = (key: string, value: unknown) => setValues((previous) => ({ ...previous, [key]: value }))

  const renderField = (entry: (typeof formModel.known)[number]) => {
    const editable = isEditable(entry.field) && !readOnly
    const supporting = !isEditable(entry.field) && entry.field.protected
      ? `${t('edit.protectedField')} ${t('edit.protectedFieldReason')}`
      : !entry.isKnownType ? t('edit.unknownFieldType', { type: entry.field.type }) : undefined
    return <FieldControl key={entry.path} field={entry.field} value={values[entry.path]} supporting={supporting} disabled={!editable}
      onChange={(value) => setValue(entry.path, value)}/>
  }

  const renderSeoFields = () => seoEntries.flatMap((entry) => {
    if (entry.field.type.toLowerCase() !== 'object' || !entry.field.of?.length) return [renderField(entry)]
    const objectValue = values[entry.path] && typeof values[entry.path] === 'object' ? values[entry.path] as Record<string, unknown> : {}
    return entry.field.of.map((child) => <FieldControl key={`${entry.path}.${child.key}`} field={child} value={objectValue[child.key]} disabled={!isEditable(child) || readOnly}
      onChange={(next) => setValue(entry.path, { ...objectValue, [child.key]: next })}/>)
  })

  // The Google preview reads the same places the publisher does, the declared ones first, then what the import brought.
  const meta = isBag(values.meta) ? values.meta : {}
  const seoBag = isBag(values.seo) ? values.seo : {}
  const previewTitle = firstText(meta.title, values.seoTitle, extraSeo?.title, seoBag.title) || title
  const previewDescription = firstText(meta.description, values.seoDescription, extraSeo?.description, seoBag.description, values.description, values.excerpt)
  const hiddenFromSearch = [meta.noindex, extraSeo?.noindex, seoBag.noindex, values.noindex].some((flag) => flag === true)
  const clip = (text: string, max: number) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text)
  const description = clip(plainTextOf(previewDescription), 160)

  const dirtyNames = [...new Set(changedLabels)]
  const dirtySummary = [...dirtyNames.slice(0, 3), ...(dirtyNames.length > 3 ? ['…'] : []), ...(statusDirty ? [t('edit.workflow')] : [])].join(', ')

  const moreItems: MenuEntry[] = [
    { key: 'history', label: t('list.openHistory'), icon: 'history', to: paths.contentHistory(siteId, id) },
    ...(!readOnly ? [content.status === 'trashed'
      ? { key: 'restore', label: t('edit.restore'), icon: 'restore_from_trash' as const, disabled: busy || dirty, onSelect: () => void runAction(() => restoreContent(id, { reason: resolveReason(note, t('reason.restore')), expectedVersion: content.version }, newIdempotencyKey()), t('edit.restored')), divider: true }
      : { key: 'trash', label: t('edit.trash'), icon: 'delete' as const, danger: true, disabled: busy || dirty, onSelect: () => setConfirmingTrash(true), divider: true }] : []),
  ]

  const tabHref = (tab: ContentTab) => paths.content(siteId, id, { tab, upTo })

  return <div className="page page-editor">
    <DirtyNavigationGuard dirty={dirty && !busy}/>
    <PageHeader
      back={{ to: paths.contents(siteId), label: t('edit.backToList') }}
      eyebrow={`${typeName} · ${content.locale.toUpperCase()}`}
      title={title}
      thumb={<Thumb size="md" path={mainImageOfContent(values, contentType?.seo?.image)?.path ?? null} image={content.mainImage && !dirty ? content.mainImage : undefined} icon={contentTypeIcon(content.contentTypeKey)} alt={t('mainImage.alt', { title })}/>}
      subtitle={<span className="inline-meta"><StatusChip tone={contentStatusTone(content.status)}>{t(`status.${content.status}`)}</StatusChip><span className="mono">{path ?? `/${content.slug}`}</span></span>}
      actions={<>
        {url && <ExternalButton href={url} title={content.status === 'published' ? undefined : t('live.notPublishedYet')}>{t('live.openOnSite')}</ExternalButton>}
        {canWrite && !viewingPast && <EditOnSiteButton siteId={siteId} path={path} online={content.status === 'published'} primaryDomain={site?.primaryDomain}/>}
        <LinkButton variant="outlined" icon="web" to={paths.contentPage(siteId, id)}>{t('page.openInPage')}</LinkButton>
        <MenuButton label={t('common.more')} items={moreItems}/>
      </>}
    />

    {viewingPast && <Banner tone="info" title={t('history.viewingAt', { version: upTo })} action={<LinkButton variant="text" to={paths.content(siteId, id)}>{t('history.backToCurrent')}</LinkButton>}/>}
    {!canWrite && !viewingPast && <Banner tone="info">{t('edit.readOnlyRole')}</Banner>}
    {!contentType && <Banner tone="warning">{t('edit.unknownType', { type: content.contentTypeKey })}</Banner>}
    {content.status === 'trashed' && <Banner tone="warning" title={t('edit.inTrash')}>{t('edit.inTrashHelp')}</Banner>}
    {justSaved && !dirty && content.status === 'published' && <Banner tone="success" title={t('edit.savedNotOnlineTitle')} action={canPublish ? <Button variant="filled" icon="rocket_launch" onClick={() => setPublishing(true)}>{t('list.publishSite')}</Button> : undefined}>
      {t('edit.savedNotOnline')}
    </Banner>}
    {saveError && <Banner tone="error">{saveError}</Banner>}

    <div className="editor-layout">
      <form className="editor-main editor-stack" onSubmit={(event) => { event.preventDefault(); void handleSave() }}>
        <Card id="sezione-titolo" title={t('editor.headline')} icon="edit_note">
          <div className="headline-grid">
            {hasTitle && <div className={cx(!showImage && 'full')}>
              {titleEntry
                ? <FieldControl field={titleEntry.field} value={values.title} disabled={!isEditable(titleEntry.field) || readOnly} onChange={(value) => setValue('title', value)}/>
                : <TextField label={t('editor.titleLabel')} value={typeof values.title === 'string' ? values.title : ''} placeholder={t('editor.titlePlaceholder')} disabled={readOnly} onChange={(event) => setValue('title', event.target.value)}/>}
            </div>}
            {showImage && <div className={cx(!hasTitle && 'full')}>
              <MediaFieldControl
                large
                label={t('editor.mainImage')}
                supporting={t('editor.mainImageHelp')}
                value={imageKey ? getAtPath(values, imageKey) : mainImage?.path}
                disabled={!imageEditable}
                onChange={(value) => { if (imageKey) setValues((previous) => setAtPath(previous, imageKey, value)) }}
              />
            </div>}
            {!hasTitle && !showImage && <p className="muted full">{t('edit.noDeclaredFields')}</p>}
          </div>
        </Card>

        <Card id="sezione-stato" title={t('editor.status')} icon="check_circle">
          {content.status === 'trashed' ? <p className="muted">{t('edit.inTrashHelp')}</p> : <>
            <div className="chip-row" role="group" aria-label={t('edit.workflow')}>
              {WORKFLOW.map((status) => <FilterChip key={status} selected={pendingStatus === status} disabled={readOnly || busy} onClick={() => setPendingStatus(status)}>{t(`status.${status}`)}</FilterChip>)}
            </div>
            <p className="field-support">{statusDirty ? t('edit.statusPending', { status: t(`status.${pendingStatus}`) }) : t(`edit.statusHelp.${content.status}`)}</p>
          </>}
        </Card>

        {content.status !== 'trashed' && <ScheduleCard
          content={content}
          siteId={siteId}
          readOnly={readOnly}
          blocked={dirty || busy}
          note={note}
          onSaved={load}
        />}

        <Card id="sezione-contenuto" title={t('editor.content')} subtitle={t('edit.contentHelp')} icon="article">
          <div className="form-stack">
            {contentEntries.map(renderField)}
            <OtherFieldsSection
              fields={otherExtras}
              values={values}
              onChange={setValue}
              disabled={readOnly}
              bodyFormat={bodyFormat}
              omitInside={extraSeo ? { extra: new Set(['seo']) } : undefined}
            />
            {contentEntries.length === 0 && otherExtras.length === 0 && <p className="muted">{t('edit.noDeclaredFields')}</p>}
          </div>
        </Card>

        <Card id="sezione-seo" title={t('editor.seo')} subtitle={t('edit.seoHelp')} icon="search">
          <div className={cx('google-preview', hiddenFromSearch && 'is-hidden')} role="group" aria-label={t('editor.previewGoogle')}>
            <span className="google-url">{displayDomain(site?.primaryDomain)}{path ?? `/${content.slug}`}</span>
            <strong className="google-title">{clip(previewTitle, 60)}</strong>
            <p className="google-text">{description || t('editor.noDescription')}</p>
          </div>
          <div className="form-stack">
            {renderSeoFields()}
            {seoExtras.length > 0 && <OtherFieldsSection fields={seoExtras} values={values} onChange={setValue} disabled={readOnly} bodyFormat={bodyFormat}/>}
            {extraSeo && <ObjectFields value={extraSeo} disabled={readOnly} bodyFormat={bodyFormat}
              onChange={(next) => setValues((previous) => ({ ...previous, extra: { ...(isBag(previous.extra) ? previous.extra : {}), seo: next } }))}/>}
            {seoEntries.length === 0 && seoExtras.length === 0 && !extraSeo && <p className="muted">{t('edit.noSeoFields')}</p>}
          </div>
        </Card>

        <ContentTranslationsCard siteId={siteId} contentId={content.id} group={content.translationGroup}/>

        {!readOnly && <Card id="sezione-strumenti" title={t('editor.tools')} icon="auto_awesome">
          <GroupBox label={t('ai.title')} hint={t('ai.description')} depth={1} defaultOpen={jumpTo === 'strumenti'} onOpenChange={setToolsOpen}>
            <div className="form-stack">
              <div className="form-grid">
                <SelectField label={t('ai.purpose')} value={aiPurpose} onChange={(event) => setAiPurpose(event.target.value === 'review' ? 'review' : 'write')}>
                  <option value="write">{t('ai.write')}</option>
                  <option value="review">{t('ai.review')}</option>
                </SelectField>
                <SelectField label={t('ai.account')} value={aiAccountId} onChange={(event) => setAiAccountId(event.target.value)}>
                  <option value="">{t('ai.chooseAccount')}</option>
                  {aiAccounts.map((account) => <option key={account.id} value={account.id} disabled={!['ready', 'available'].includes(account.status)}>{account.label}{account.reason ? ` — ${account.reason}` : ''}</option>)}
                </SelectField>
                <SelectField label={t('ai.field')} value={aiPath} onChange={(event) => setAiPath(event.target.value)}>
                  <option value="">{t('ai.chooseField')}</option>
                  {formModel.known.filter((entry) => isEditable(entry.field)).map((entry) => <option key={entry.path} value={entry.path}>{fieldLabel(entry.field, locale)}</option>)}
                </SelectField>
              </div>
              <TextArea label={t('ai.promptLabel')} rows={5} value={aiPrompt} onChange={(event) => setAiPrompt(event.target.value)} placeholder={t('ai.prompt')} supporting={t('ai.promptHelp')}/>
              <div className="section-row">
                <span className="muted">{dirty ? t('edit.unsaved') : ''}</span>
                <Button variant="filled" icon="auto_awesome" disabled={busy || dirty || !aiAccountId || !aiPath || !aiPrompt.trim()} onClick={() => void handleAi()}>{aiBusy ? t('ai.working') : t('ai.generate')}</Button>
              </div>
            </div>
          </GroupBox>
        </Card>}

        {technical.length > 0 && <Card className="editor-technical" title={t('editor.technical')} icon="code">
          <GroupBox label={t('editor.technical')} hint={t('editor.technicalHelp')} depth={1} defaultOpen={false}>
            <TechnicalDetails entries={technical}/>
          </GroupBox>
        </Card>}

        {!viewingPast && <RepositoryMappingPanel
          aggregateType="Content"
          resourceId={content.id}
          resourceVersion={content.version}
          canWrite={canWrite}
          primaryDirty={primaryDirty}
          onDirtyChange={setRepositoryDirty}
          onExpandedChange={setRepositoryExpanded}
          onVersionChange={(version) => setContent((current) => current ? { ...current, version } : current)}
        />}
      </form>

      <aside className="editor-side" aria-label={t('edit.details')}>
        <Card title={t('edit.address')} icon="link">
          <Facts items={[
            { label: t('edit.slug'), value: <span className="mono">{content.slug}</span> },
            { label: t('edit.publicAddress'), value: url ? <a href={url} target="_blank" rel="noopener noreferrer" className="break">{displayDomain(site?.primaryDomain)}{path}</a> : <span className="muted">{t('live.noAddress')}</span> },
          ]}/>
          <div className="card-footer-actions">
            {url && <ExternalButton variant="text" href={url}>{t('live.openOnSite')}</ExternalButton>}
            <LinkButton variant="text" icon="web" to={paths.contentPage(siteId, id)}>{t('page.preview')}</LinkButton>
          </div>
          {url && content.status !== 'published' && <p className="field-support">{t('live.notPublishedYet')}</p>}
        </Card>

        <Card title={t('edit.details')} icon="info">
          <Facts items={[
            { label: t('edit.type'), value: typeName },
            { label: t('edit.locale'), value: content.locale.toUpperCase() },
            { label: t('edit.versionLabel'), value: content.version },
            ...(content.publishedAt ? [{ label: t('edit.publishedAt'), value: absoluteDateTime(content.publishedAt, locale) }] : []),
            ...(content.writtenWith ? [{ label: t('edit.writtenWith'), value: content.writtenWith }] : []),
            ...(content.reviewedWith ? [{ label: t('edit.reviewedWith'), value: content.reviewedWith }] : []),
          ]}/>
          <div className="card-footer-actions">
            <LinkButton variant="text" icon="history" to={paths.contentHistory(siteId, id)}>{t('list.openHistory')}</LinkButton>
          </div>
        </Card>
      </aside>
    </div>

    {!readOnly && showPrimarySavebar(repositoryExpanded, primaryDirty) && <footer className="savebar" aria-label={t('edit.saveBar')}>
      <span className={`save-state ${dirty ? 'is-dirty' : 'is-clean'}`}>{saving ? t('edit.saving') : dirty ? (dirtySummary ? t('editor.unsavedBanner', { fields: dirtySummary }) : t('edit.unsavedShort')) : t('edit.noChanges')}</span>
      <ReasonNote value={note} onChange={setNote} fallback={previewReason} disabled={busy}/>
      <div className="savebar-actions">
        <Button variant="text" disabled={!primaryDirty || busy} onClick={() => { setValues({ ...content.data }); setPendingStatus(content.status); setSaveError(null) }}>{t('edit.discard')}</Button>
        {content.status !== 'published' && content.status !== 'trashed' && canPublishStatus(pendingStatus) && <Button variant="outlined" icon="check_circle" disabled={busy || repositoryDirty} onClick={() => void handleSave('published')}>{t('edit.saveAndMarkPublished')}</Button>}
        <Button variant="filled" icon="check" disabled={busy || repositoryDirty || !primaryDirty} onClick={() => void handleSave()}>{saving ? t('edit.saving') : t('edit.save')}</Button>
      </div>
    </footer>}

    {confirmingTrash && <ConfirmDialog
      title={t('edit.trash')}
      message={t('edit.confirmTrash')}
      confirmLabel={t('edit.trash')}
      cancelLabel={t('edit.cancel')}
      danger
      onConfirm={() => { setConfirmingTrash(false); void runAction(() => trashContent(id, { reason: resolveReason(note, t('reason.trash')), expectedVersion: content.version }, newIdempotencyKey()), t('edit.trashed')) }}
      onCancel={() => setConfirmingTrash(false)}
    />}

    {conflict && <ConflictDialog conflict={conflict.info} busy={saving} onKeepMine={() => void handleKeepMine()} onKeepTheirs={handleKeepTheirs} onDismiss={handleKeepTheirs}/>}
    {publishing && site && <PublishDialog siteId={site.id} siteName={site.name} onClose={() => setPublishing(false)} onPublished={() => { setJustSaved(false); void reloadWorkspace() }}/>}
  </div>
}

/** "Salva e segna come pubblicato" is offered unless the person already chose another status by hand. */
function canPublishStatus(pending: Status): boolean {
  return pending !== 'trashed'
}

function contentTitle(content: ContentDetail): string {
  for (const key of ['title', 'headline', 'name', 'tagline']) {
    const value = content.data[key]
    if (typeof value === 'string' && value.trim()) return value
  }
  return content.title || content.slug
}

function isSeoPath(path: string): boolean {
  const normalized = path.toLowerCase().replaceAll('/', '.')
  return normalized === 'meta' || normalized.startsWith('meta.') || [
    'seo', 'seotitle', 'seodescription', 'canonical', 'noindex', 'robots', 'ogtitle', 'ogdescription', 'summarybox', 'faqitems',
  ].some((key) => normalized === key || normalized.startsWith(`${key}.`))
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function firstText(...candidates: unknown[]): string {
  for (const candidate of candidates) if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  return ''
}

/** How the body text was written, when the import said so: HTML bodies get the visual editor. */
function bodyFormatOf(values: Bag): string | undefined {
  const own = values.bodyFormat
  if (typeof own === 'string') return own
  const nested = isBag(values.extra) ? values.extra.bodyFormat : undefined
  return typeof nested === 'string' ? nested : undefined
}
